import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Input, Select, Space, Empty, message, Typography } from 'antd';
import { useReferential } from '../../../context/ReferentialContext';
import { CODIFICATION_TYPE } from '../../../constant/codification';
import ProjectCard from '../../../components/cards/projects/ProjectCard';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { TProject, TProjectLike } from '../../../models/project/project';
import { useProjectApi } from '../../../api/projects/projects';
import { useTilmiAuth } from '../../../hooks/useTilmiAuth';

const { Search } = Input;
const { Option } = Select;

const getSchoolYear = (iso: string) => {
    const d = new Date(iso);
    const y = d.getFullYear();
    return d.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

/**
 * TilmiStore – vue catalogue globale (tous les projets « ACTIVE » de toutes les écoles)
 */
const TilmiStore: React.FC = () => {

    const { t } = useTranslation();
    const { getTilmicodeStore, getLikesByUserId } = useProjectApi();
    const { getCodificationsByCodeType } = useReferential();
    const { getUserAppId } = useTilmiAuth();
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState<TProject[]>([]);
    const [likes, setLikes] = useState<TProjectLike[]>([]);
    /* filtres */
    const [searchProject, setSearchProject] = useState('');
    const [filterType, setFilterType] = useState<number | null>(null);
    const [filterYear, setFilterYear] = useState<string | null>(null);


    /* Chargement */
    useEffect(() => {
        setLoading(true)
        const userId = getUserAppId();   // ← on récupère l'ID de l'utilisateur
        Promise.all([getTilmicodeStore(), getLikesByUserId(userId)])
            .then(([projRes, likesRes]) => {
                const projs = projRes.data ?? [];
                const lks = likesRes.data ?? [];
                setLikes(lks)
                setProjects(projs);
            })
            .catch(err => message.error(err.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    /* Codifs type projet */
    const projectTypeCodifs = useMemo(
        () => getCodificationsByCodeType(CODIFICATION_TYPE.PROJECT_TYPE),
        [getCodificationsByCodeType]
    );

    /* Filtrage */
    const filtered = useMemo(() =>
        projects.filter(p => {
            if (searchProject && !p.name.toLowerCase().includes(searchProject.toLowerCase())) return false;
            if (filterType && p.project_type_id !== filterType) return false;
            if (filterYear && p.created_at && getSchoolYear(p.created_at) !== filterYear) return false;
            return true;
        }),
        [projects, searchProject, filterType, filterYear]);

    /* Dropdown dynamiques */
    const projectTypes = useMemo(() =>
        Array.from(new Map(projects
            .filter(p => p.project_type_id)
            .map(p => {
                const id = p.project_type_id;
                const codif = projectTypeCodifs.find(c => c.id === id);
                return [id, codif?.label ?? ''];
            }))).map(([id, label]) => ({ id, label })),
        [projects, projectTypeCodifs]);

    const schoolYears = useMemo(() =>
        Array.from(new Set(projects
            .filter(p => p.created_at)
            .map(p => getSchoolYear(p.created_at || "_")))).sort(),
        [projects]);

    return (
        <AppLayout title={t('store.title')} loading={loading}>
            <div style={{ padding: '24px 16px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>
                <section
                    style={{
                        borderRadius: 28,
                        padding: '28px 24px',
                        background: 'linear-gradient(135deg, rgba(45,156,201,0.08), rgba(139,190,30,0.06))',
                        border: '1px solid rgba(45,156,201,0.18)',
                        boxShadow: '0 20px 45px rgba(7,24,38,0.12)',
                        backdropFilter: 'blur(14px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 20,
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            {t('store.title')}
                        </Typography.Title>
                        <Typography.Text style={{ color: 'rgba(12,24,42,0.65)' }}>
                            {t('store.subtitle', 'Explore les créations Tilmi partagées par toutes les écoles. Filtre par type ou année pour trouver un projet.')} 
                        </Typography.Text>
                    </div>
                    <Space wrap size={16}>
                        <Search
                            placeholder={t('store.searchProject')}
                            onSearch={setSearchProject}
                            allowClear
                            style={{ minWidth: 240 }}
                            size="large"
                        />
                        <Select
                            placeholder={t('store.projectType')}
                            onChange={setFilterType}
                            allowClear
                            style={{ minWidth: 200 }}
                            value={filterType ?? undefined}
                            size="large"
                        >
                            {projectTypes.map((pt) => (
                                <Option key={pt.id} value={pt.id}>
                                    {pt.label}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            placeholder={t('store.schoolYear')}
                            onChange={setFilterYear}
                            allowClear
                            style={{ minWidth: 200 }}
                            value={filterYear ?? undefined}
                            size="large"
                        >
                            {schoolYears.map((y) => (
                                <Option key={y} value={y}>
                                    {y}
                                </Option>
                            ))}
                        </Select>
                    </Space>
                </section>

                {filtered.length === 0 ? (
                    <div
                        style={{
                            borderRadius: 28,
                            padding: '40px 16px',
                            background: '#fff',
                            border: '1px solid rgba(45,156,201,0.18)',
                            boxShadow: '0 16px 32px rgba(7,24,38,0.08)',
                        }}
                    >
                        <Empty description={t('text.noProject', 'Aucun projet')} />
                    </div>
                ) : (
                    <Row gutter={[20, 24]}>
                        {filtered.map((p) => (
                            <Col key={p.id} xs={24} sm={12} md={8} lg={6} xl={4}>
                                <ProjectCard studentProject={p} likedStatus={likes} showTags />
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        </AppLayout>
    );
};

export default TilmiStore;
